import { CardContent, Typography, Stack, LinearProgress } from '@mui/material';
import { SettingBox, LeftSide, StyledCard, Subtitle, StyledSection } from '../styles';
import { useCurrentMonthVideoCount } from '@/hooks/useCurrentMonthVideoCount';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';

const PlanUsageCard = () => {
  // Use hooks that properly handle both rate-based and lifetime cap plans
  const { videoCount, limit, label, planType, isLoading: countLoading } = useCurrentMonthVideoCount();
  const { planName, isLoading: featuresLoading, maxAiNotes, aiProcessedCount } = useCurrentPlanFeatures();

  const isLoading = countLoading || featuresLoading;
  const isUnlimited = limit === 0;
  const limitDisplay = isUnlimited ? 'Unlimited' : limit;
  const usagePercentage = isUnlimited ? 0 : (videoCount / limit) * 100;
  const isLifetimePlan = planType === 'lifetime_cap';

  // AI processing cap is shown only when the plan has a finite trial cap.
  // Paid (unlimited) plans hide this row entirely.
  const hasAiCap = typeof maxAiNotes === 'number' && maxAiNotes > 0;
  const aiUsagePercentage = hasAiCap ? Math.min((aiProcessedCount / (maxAiNotes as number)) * 100, 100) : 0;
  const aiCapReached = hasAiCap && aiProcessedCount >= (maxAiNotes as number);

  return (
    <SettingBox>
      <LeftSide>
        <Typography variant="body1" fontWeight="700">
          Plan Usage
        </Typography>
        <Subtitle variant="body2">Here's an overview of your current plan's usage.</Subtitle>
      </LeftSide>
      <StyledCard>
        <CardContent>
          <StyledSection>
            <Typography variant="h6" gutterBottom>
              {label} ({isLoading ? 'Loading...' : planName})
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body1">
                {videoCount} / {limitDisplay}
              </Typography>
              {!isUnlimited && (
                <LinearProgress variant="determinate" value={usagePercentage} sx={{ height: 10, borderRadius: 5 }} />
              )}
              {!isUnlimited && videoCount >= limit && (
                <Typography variant="body2" color="error">
                  {isLifetimePlan
                    ? 'Note creation limit reached. Upgrade to continue.'
                    : 'You have reached your note creation limit. Upgrade your plan to create more notes!'}
                </Typography>
              )}
              {isUnlimited && (
                <Typography variant="body2" color="text.secondary">
                  You have unlimited note creation with your current plan.
                </Typography>
              )}
            </Stack>
          </StyledSection>

          {hasAiCap && !isLoading && (
            <StyledSection>
              <Typography variant="h6" gutterBottom>
                AI Processing Trial
              </Typography>
              <Stack spacing={1}>
                <Typography variant="body1">
                  {aiProcessedCount} / {maxAiNotes} AI-processed notes used
                </Typography>
                <LinearProgress variant="determinate" value={aiUsagePercentage} sx={{ height: 10, borderRadius: 5 }} />
                {aiCapReached ? (
                  <Typography variant="body2" color="error">
                    You've used your free AI processing trial. New recordings will still upload but won't be transcribed
                    or summarized. Upgrade for unlimited AI.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Lifetime trial — counter never resets.
                  </Typography>
                )}
              </Stack>
            </StyledSection>
          )}
        </CardContent>
      </StyledCard>
    </SettingBox>
  );
};

export default PlanUsageCard;
